import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import Card from '@applied/dragonfruit/src/components/Card/Card';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import LoadingSpinner from '@applied/dragonfruit/src/components/LoadingSpinner/LoadingSpinner';
import { useSnackbar } from '@applied/dragonfruit/src/components/PageSnackbar/snackbar_provider';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { Currency, formatSubdivisions } from 'api/compensation';
import { checkExists } from 'api/typing';

import {
  DULL_BLUE,
  hslToString,
  interpolateHSLColor,
  SATURATED_BLUE,
} from 'components/common/color';
import { Divider } from 'components/common/Divider/Divider';
import { CompensationSlider } from 'components/WebOfferPage/SliderSection/CompensationSlider/CompensationSlider';
import { AnnualBonusType } from 'components/WebOfferPage/SliderSection/slider_section_classes';
import {
  updateSalarySubdivisions,
  updateSignOnBonusSubdivisions,
} from 'components/WebOfferPage/SliderSection/slider_section_requests';
import { useSliderSubdivisions } from 'components/WebOfferPage/SliderSection/use_slider_subdivisions';
import { calculateEquity, useWebOffer } from 'components/WebOfferPage/web_offer_context';
import type { WebOfferPageParams } from 'components/WebOfferPage/WebOfferPage';

import './slider-section.scss';

const VESTING_SCHEDULE_YEARS = 4;
const PERCENT_TO_RATIO = 0.01;
const STATIC_VALUE_COLOR = hslToString(DULL_BLUE);

export function SliderSection(): JSX.Element {
  const history = useHistory();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { candidateEmail } = useParams<WebOfferPageParams>();
  const {
    selections: { salarySubdivisions, signOnBonusSubdivisions },
    compensationInfo: {
      currency,
      baseSalarySubdivisions,
      salaryThresholdPercentage,
      salaryEquityRatio,
      maxSignOnBonusSubdivisions,
      signOnBonusEquityRatio,
      relocationBonusSubdivisions,
      annualBonusType,
      annualBonusValue,
      isSalaryStatic,
      isSignOnBonusStatic,
    },
    marketConditions,
    setSalarySubdivisions,
    setSignOnBonusSubdivisions,
    equityCalculationConstants,
    equityUsdCents,
    selection: {
      isFinal,
      salarySubdivisions: selectionSalarySubdivisions,
      signOnBonusSubdivisions: selectionSignOnBonusSubdivisions,
      setSalarySubdivisions: setSelectionSalarySubdivisions,
      setSignOnBonusSubdivisions: setSelectionSignOnBonusSubdivisions,
    },
    isRequestingOfferLetter,
  } = useWebOffer();

  const { handleChange: handleSalarySubdivisionsChange } = useSliderSubdivisions({
    fieldName: 'salarySubdivisions',
    updateFunction: (updateHistory, value) =>
      updateSalarySubdivisions(updateHistory, value, candidateEmail),
    setDisplayValue: setSalarySubdivisions,
    errorMessage: 'Failed to update salary',
    committedValue: selectionSalarySubdivisions,
    setCommittedValue: setSelectionSalarySubdivisions,
    history,
    queryClient,
    snackbar,
  });

  const { handleChange: handleSignOnBonusSubdivisionsChange } = useSliderSubdivisions({
    fieldName: 'signOnBonusSubdivisions',
    updateFunction: (updateHistory, value) =>
      updateSignOnBonusSubdivisions(updateHistory, value, candidateEmail),
    setDisplayValue: setSignOnBonusSubdivisions,
    errorMessage: 'Failed to update sign-on bonus',
    committedValue: selectionSignOnBonusSubdivisions,
    setCommittedValue: setSelectionSignOnBonusSubdivisions,
    history,
    queryClient,
    snackbar,
  });

  const [minSalarySubdivisions, maxSalarySubdivisions, minEquityUsdCents, maxEquityUsdCents] =
    useMemo(() => {
      if (
        !checkExists(equityCalculationConstants) ||
        !checkExists(baseSalarySubdivisions) ||
        !checkExists(salaryThresholdPercentage) ||
        !checkExists(maxSignOnBonusSubdivisions) ||
        (isSignOnBonusStatic && !checkExists(signOnBonusSubdivisions))
      ) {
        return [undefined, undefined, undefined, undefined];
      }

      // The amount salary is able to deviate from the base salary.
      const salaryDeltaSubdivisions =
        Number(baseSalarySubdivisions) * Number(salaryThresholdPercentage) * PERCENT_TO_RATIO;

      // Salary bounds
      const minSalary = Number(baseSalarySubdivisions) - salaryDeltaSubdivisions;
      const maxSalary = Number(baseSalarySubdivisions) + salaryDeltaSubdivisions;

      const salaryForMinEquity = isSalaryStatic ? Number(baseSalarySubdivisions) : maxSalary;
      const salaryForMaxEquity = isSalaryStatic ? Number(baseSalarySubdivisions) : minSalary;
      const bonusForMinEquity = isSignOnBonusStatic
        ? Number(signOnBonusSubdivisions)
        : Number(maxSignOnBonusSubdivisions);
      const bonusForMaxEquity = isSignOnBonusStatic ? Number(signOnBonusSubdivisions) : 0;

      const minEquity = calculateEquity(
        {
          selectedSalarySubdivisions: salaryForMinEquity,
          selectedSignOnBonusSubdivisions: bonusForMinEquity,
        },
        equityCalculationConstants,
      );
      const maxEquity = calculateEquity(
        {
          selectedSalarySubdivisions: salaryForMaxEquity,
          selectedSignOnBonusSubdivisions: bonusForMaxEquity,
        },
        equityCalculationConstants,
      );
      return [minSalary, maxSalary, minEquity, maxEquity];
    }, [
      baseSalarySubdivisions,
      salaryThresholdPercentage,
      maxSignOnBonusSubdivisions,
      equityCalculationConstants,
      isSalaryStatic,
      isSignOnBonusStatic,
      signOnBonusSubdivisions,
    ]);

  const annualBonusSubdivisions = useMemo(() => {
    if (
      !checkExists(annualBonusType) ||
      !checkExists(annualBonusValue) ||
      !checkExists(salarySubdivisions)
    ) {
      return undefined;
    }

    return annualBonusType === AnnualBonusType.NONE
      ? 0
      : annualBonusType === AnnualBonusType.FIXED_AMOUNT
        ? Number(annualBonusValue)
        : Number(annualBonusValue) * PERCENT_TO_RATIO * Number(salarySubdivisions);
  }, [annualBonusType, annualBonusValue, salarySubdivisions]);

  const totalCompensationSubdivisions = useMemo(() => {
    if (
      !checkExists(marketConditions) ||
      !checkExists(marketConditions.usdCentsToOfferSubdivisions) ||
      !checkExists(salarySubdivisions) ||
      !checkExists(signOnBonusSubdivisions) ||
      !checkExists(equityUsdCents) ||
      !checkExists(relocationBonusSubdivisions) ||
      !checkExists(annualBonusSubdivisions)
    ) {
      return undefined;
    }
    return (
      (equityUsdCents * marketConditions.usdCentsToOfferSubdivisions) / VESTING_SCHEDULE_YEARS +
      salarySubdivisions +
      signOnBonusSubdivisions +
      Number(relocationBonusSubdivisions) +
      annualBonusSubdivisions
    );
  }, [
    equityUsdCents,
    salarySubdivisions,
    signOnBonusSubdivisions,
    marketConditions,
    relocationBonusSubdivisions,
    annualBonusSubdivisions,
  ]);

  // When a non-static slider has zero range (e.g., salaryThresholdPercentage is 0,
  // or signOnBonusEquityRatio is 0), treat it as static for display purposes
  // rather than blocking the page with a loading spinner.
  const showSalaryAsStatic = isSalaryStatic || minSalarySubdivisions === maxSalarySubdivisions;
  const showEquityAsStatic =
    (isSalaryStatic && isSignOnBonusStatic) || minEquityUsdCents === maxEquityUsdCents;
  const showSignOnBonusAsStatic = isSignOnBonusStatic;

  if (
    !checkExists(marketConditions) ||
    !checkExists(salarySubdivisions) ||
    !checkExists(signOnBonusSubdivisions) ||
    !checkExists(minSalarySubdivisions) ||
    !checkExists(maxSalarySubdivisions) ||
    !checkExists(minEquityUsdCents) ||
    !checkExists(maxEquityUsdCents) ||
    !checkExists(totalCompensationSubdivisions) ||
    !checkExists(equityUsdCents) ||
    !checkExists(currency) ||
    !checkExists(maxSignOnBonusSubdivisions) ||
    !checkExists(relocationBonusSubdivisions) ||
    !checkExists(annualBonusSubdivisions)
  ) {
    return (
      <Flex alignment="center" justify="center" padding="xxl">
        <LoadingSpinner shouldCenterHorizontally shouldCenterVertically />
      </Flex>
    );
  }
  const equityRatio =
    maxEquityUsdCents === minEquityUsdCents
      ? 0
      : (equityUsdCents - minEquityUsdCents) / (maxEquityUsdCents - minEquityUsdCents);
  const sliderColor = hslToString(interpolateHSLColor(DULL_BLUE, SATURATED_BLUE, equityRatio));
  const bothStatic = isSalaryStatic && isSignOnBonusStatic;
  const staticValueColor = bothStatic ? STATIC_VALUE_COLOR : sliderColor;

  return (
    <Flex
      className="slider-section-container"
      alignment="center"
      justify="center"
      gap="xxl"
      padding="xxl"
    >
      <div id="compensation">
        <Flex className="slider-section" direction="column" gap="lg">
          <Flex className="header-section" direction="column" alignment="left">
            <Typography className="section-title" variant="title">
              COMPENSATION
            </Typography>
            <Typography className="section-header" variant="header">
              Let&apos;s build your offer
            </Typography>
          </Flex>
          <Flex className="body-container" direction="column" gap="lg">
            {/* NOTE: We don't allow modification of sliders when the equtiy ratio is 0.
                Especially for sign-on bonus, allowing them to lose bonus for no gain could seem unfair. */}
            {showSalaryAsStatic ? (
              <Flex direction="row" justify="space-between">
                <Typography className="slider-label" variant="subtitle">
                  Base salary
                </Typography>
                <Typography className="slider-value" variant="header">
                  <span style={{ color: staticValueColor }}>
                    {formatSubdivisions(salarySubdivisions, currency as Currency)}
                  </span>
                </Typography>
              </Flex>
            ) : (
              <CompensationSlider
                label="Base salary"
                valueSubdivisions={salarySubdivisions}
                setValueSubdivisions={handleSalarySubdivisionsChange}
                minSubdivisions={minSalarySubdivisions}
                maxSubdivisions={maxSalarySubdivisions}
                currency={currency as Currency}
                editable={
                  !isFinal &&
                  !isRequestingOfferLetter &&
                  checkExists(salaryEquityRatio) &&
                  salaryEquityRatio > 0
                }
                color={sliderColor}
              />
            )}
            <Divider orientation="horizontal" />
            {showEquityAsStatic ? (
              <Flex direction="row" justify="space-between">
                <Typography className="slider-label" variant="subtitle">
                  Equity
                  <a className="text-style-link" href="#disclaimer">
                    *
                  </a>
                </Typography>
                <Typography className="slider-value" variant="header">
                  <span style={{ color: STATIC_VALUE_COLOR }}>
                    {formatSubdivisions(equityUsdCents, Currency.USD)}
                  </span>
                </Typography>
              </Flex>
            ) : (
              <CompensationSlider
                label={
                  <>
                    <span>Equity</span>
                    <a className="text-style-link" href="#disclaimer">
                      *
                    </a>
                  </>
                }
                valueSubdivisions={equityUsdCents}
                minSubdivisions={minEquityUsdCents}
                maxSubdivisions={maxEquityUsdCents}
                currency={Currency.USD}
                color={sliderColor}
              />
            )}
            <Divider orientation="horizontal" />
            {maxSignOnBonusSubdivisions > 0 && (
              <>
                {showSignOnBonusAsStatic ? (
                  <Flex direction="row" justify="space-between">
                    <Typography className="slider-label" variant="subtitle">
                      Sign-on bonus
                    </Typography>
                    <Typography className="slider-value" variant="header">
                      <span style={{ color: staticValueColor }}>
                        {formatSubdivisions(signOnBonusSubdivisions, currency as Currency)}
                      </span>
                    </Typography>
                  </Flex>
                ) : (
                  <CompensationSlider
                    label="Sign-on bonus"
                    valueSubdivisions={signOnBonusSubdivisions}
                    setValueSubdivisions={handleSignOnBonusSubdivisionsChange}
                    minSubdivisions={0}
                    maxSubdivisions={maxSignOnBonusSubdivisions}
                    currency={currency as Currency}
                    editable={
                      !isFinal &&
                      !isRequestingOfferLetter &&
                      checkExists(signOnBonusEquityRatio) &&
                      signOnBonusEquityRatio > 0
                    }
                    color={sliderColor}
                  />
                )}
                <Divider orientation="horizontal" />
              </>
            )}
            {annualBonusType !== AnnualBonusType.NONE &&
              checkExists(annualBonusSubdivisions) &&
              annualBonusSubdivisions > 0 && (
                <>
                  <Flex className="annual-bonus-container" direction="row" justify="space-between">
                    <Typography className="slider-label" variant="subtitle">
                      Annual bonus
                    </Typography>
                    <Typography className="slider-value" variant="header">
                      {formatSubdivisions(annualBonusSubdivisions, currency as Currency)}
                    </Typography>
                  </Flex>
                  <Divider orientation="horizontal" />
                </>
              )}
            {relocationBonusSubdivisions > 0 && (
              <>
                <Flex
                  className="relocation-bonus-container"
                  direction="row"
                  justify="space-between"
                >
                  <Typography className="slider-label" variant="subtitle">
                    Relocation bonus
                  </Typography>
                  <Typography className="slider-value" variant="header">
                    {formatSubdivisions(Number(relocationBonusSubdivisions), currency as Currency)}
                  </Typography>
                </Flex>
                <Divider orientation="horizontal" />
              </>
            )}
          </Flex>
          <Card className="section-summary-card" padding="none">
            <Flex
              className="summary-value-container"
              direction="column"
              alignment="center"
              justify="center"
              padding="xl"
            >
              <Typography className="summary-value" variant="header">
                {formatSubdivisions(totalCompensationSubdivisions, currency as Currency)}
              </Typography>
            </Flex>
            <Flex
              className="summary-label-container"
              direction="column"
              alignment="center"
              justify="center"
              padding="xl"
            >
              <Typography className="summary-label" variant="caption">
                First year total compensation
              </Typography>
            </Flex>
          </Card>
        </Flex>
      </div>
    </Flex>
  );
}
